import { findRow, formatPrice } from "@/data/pricing";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Layers } from "lucide-react";
import { ServicePage } from "@/components/site/ServicePage";
import { serviceFaqs } from "@/data/serviceFaqs";
import { canonicalLinks, ogUrl, jsonLd, serviceSchema, ogImage, breadcrumbSchema, faqPageSchema, webPageSchema, absoluteUrl } from "@/lib/seo";

const answerPrice = (name: string) => formatPrice(findRow(name));

export const Route = createFileRoute("/services/project-leadership")({
  head: () => ({
    meta: [
      { name: "twitter:card", content: "summary_large_image" },
      { title: "Project & Initiative Leadership | Ajility Fractional COO" },
      {
        name: "description",
        content:
          "Lead strategic initiatives to consistent delivery: executive sponsorship, project governance, cross-functional alignment and on-time execution.",
      },
      { property: "og:title", content: "Project & Initiative Leadership | Ajility Fractional COO" },
      {
        property: "og:description",
        content:
          "Lead strategic initiatives to consistent delivery: executive sponsorship, project governance, cross-functional alignment and on-time execution.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:title", content: "Project & Initiative Leadership | Ajility Fractional COO" },
      { name: "twitter:description", content: "Lead strategic initiatives to consistent delivery: executive sponsorship, project governance, cross-functional alignment and on-time execution." },
      ...ogImage("services-project-leadership", "Project and initiative leadership by Ajility"),
      ogUrl("/services/project-leadership"),
    ],
    links: canonicalLinks("/services/project-leadership"),
    scripts: [
      jsonLd(webPageSchema({ image: "services-project-leadership", mainEntityId: `${absoluteUrl("/services/project-leadership")}#service`, path: "/services/project-leadership", name: "Project & Initiative Leadership | Ajility Fractional COO", description: "Lead strategic initiatives to consistent delivery: executive sponsorship, project governance, cross-functional alignment and on-time execution." })),
      jsonLd(breadcrumbSchema([{ name: "Services", path: "/services" }, { name: "Project & Initiative Leadership", path: "/services/project-leadership" }])),jsonLd(serviceSchema({ name: "Project & Initiative Leadership", description: "Lead strategic initiatives to consistent delivery: executive sponsorship, project governance, cross-functional alignment and on-time execution.", path: "/services/project-leadership", slug: "project-leadership" })), jsonLd(faqPageSchema("/services/project-leadership", serviceFaqs["project-leadership"], `${absoluteUrl("/services/project-leadership")}#service`))],
  }),
  component: () => (
    <ServicePage slug="project-leadership"
      Icon={Layers}
      eyebrow="Service: Project Leadership"
      title="Project governance and initiative leadership that delivers."
      intro={<>{`Ajility can lead a software rollout as a scoped initiative ${answerPrice("Scoped per initiative")}: the plan, the weekly delivery meeting, vendor management and reporting to you or your board until it lands. Ajility sells no software and takes no vendor commission.`}{" "}Know the objective, owner, budget, risks and next decision without chasing five people for an update. Every initiative starts with a one-page project charter, and larger commitments get <Link to="/insights/$slug" params={{ slug: "mining-90-days" }} className="underline decoration-white/40 underline-offset-4 hover:decoration-frost">a costed business case before a dollar is spent</Link>.</>}
      problems={[
        "Important operational improvements stall because 'business as usual' takes over.",
        "Cross-functional projects fail due to lack of an authoritative leader.",
        "Systems implementations drag on for months over budget.",
        "Founders getting dragged into project management.",
      ]}
      installs={[
        "A one-page project charter for every initiative: objective, scope, owner, budget and the decision it serves.",
        "A costed business case before a dollar is spent on any larger commitment.",
        "Hands-on project management by a senior operator, with structured governance, milestones and risk management.",
        "Cross-functional alignment and resource allocation.",
        "Decision support for the owner throughout, not just status reports.",
        "On-time, on-budget delivery of strategic initiatives.",
      ]}
      process={[
        { title: "Charter", desc: "One page: objective, scope, owner, budget and the decision it serves. Larger commitments add a costed business case." },
        { title: "Govern", desc: "Establish steering committees and reporting rhythms." },
        { title: "Drive", desc: "Lead the execution hands-on, resolve bottlenecks and keep the people involved aligned." },
        { title: "Handover", desc: "Systemise the outcome and transition to operations." },
      ]}
    />
  ),
});
